import { useRef } from 'react';
import { FilterReportType } from '@cbo/shared-library';
import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Draggable from 'react-draggable';
import { DatasetColumn } from '../../CentralReports/components/CreateReportDialog';
import {
  editFiltersCheckboxItemSx,
  editFiltersCloseButtonSx,
  editFiltersHeaderSx,
  editFiltersPopoverContainerSx,
} from './styles';
import { NonDraggableSelectors } from '../helpers';
import { draggablePopoverBackdropSx } from '../styles';

interface EditFiltersPopoverProps {
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  onDismiss: () => void;
  filters: DatasetColumn[];
  selectedFilters: FilterReportType[];
  onToggleFilter: (filterId: FilterReportType) => void;
}

function EditFiltersPopover({
  anchorEl,
  onClose,
  onDismiss,
  filters,
  selectedFilters,
  onToggleFilter,
}: EditFiltersPopoverProps) {
  const { t } = useTranslation();
  const nodeRef = useRef(null);

  const uniqueFilters = Array.from(new Map(filters.map((filter) => [filter.filterType, filter])).values());

  return (
    <Draggable nodeRef={nodeRef} cancel={NonDraggableSelectors}>
      <Popover
        ref={nodeRef}
        slotProps={draggablePopoverBackdropSx}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        data-testid='edit-filters-popover'
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={editFiltersPopoverContainerSx}>
          <Stack>
            <Box sx={editFiltersHeaderSx}>
              <Typography variant='h6'>{t('admin.customReports.editFiltersTitle')}</Typography>
              <IconButton
                sx={editFiltersCloseButtonSx}
                onClick={onDismiss}
                data-testid='edit-filter-popover-frame-clear'
                aria-label={t('globalFilters.multiselect.closeDialog')}
              >
                <ClearIcon />
              </IconButton>
            </Box>

            <Typography variant='body2' color='text.secondary' flexWrap='wrap'>
              {t('admin.customReports.editFiltersSubtitle')}
            </Typography>
          </Stack>

          <Box>
            <FormGroup>
              {uniqueFilters.map((filter) => (
                <FormControlLabel
                  key={filter.id}
                  control={
                    <Checkbox
                      checked={selectedFilters.includes(filter.filterType as FilterReportType)}
                      data-testid={`edit-filter-${filter.id}`}
                      onChange={() => onToggleFilter(filter.filterType as FilterReportType)}
                      size='medium'
                    />
                  }
                  label={filter.label}
                  sx={editFiltersCheckboxItemSx}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      </Popover>
    </Draggable>
  );
}

export default EditFiltersPopover;
